import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { Layout } from "./components/Layout";
import { RoleGate } from "./components/RoleGate";
import { LoginPage } from "./pages/LoginPage";
import { FleetPage } from "./pages/FleetPage";
import { CabinetTypePage } from "./pages/CabinetTypePage";
import { CabinetPage } from "./pages/CabinetPage";
import { OpeningPage } from "./pages/OpeningPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { UsersPage } from "./pages/UsersPage";
import { ChangePassword } from "./components/ChangePassword";
import { ProductListPage } from "./pages/ProductListPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import DeviceConsolePage from "./pages/DeviceConsolePage";
import { PurchasesPage } from "./pages/PurchasesPage";
import { ClassifierPage } from "./pages/ClassifierPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LiveTestPage } from "./pages/LiveTestPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { DocsPage } from "./pages/DocsPage";
import { ApiDocsPage } from "./pages/ApiDocsPage";

function RequireAuth({ children }) {
  const { user } = useApp();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // A temporary password (an invitation or a reset) opens nothing but its own change.
  if (user.must_change_password) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4"><ChangePassword forced /></div>;
  }
  return children;
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            {/* The fleet replaces the dashboard (docs/tasks/35): types, cabinets, what needs a person. */}
            <Route
              index
              element={
                <RoleGate roles={["tenant_admin", "product_manager", "ml_operator", "device_operator", "auditor"]}>
                  <FleetPage />
                </RoleGate>
              }
            />
            <Route path="dashboard" element={<Navigate to="/" replace />} />
            <Route
              path="types/:typeId"
              element={
                <RoleGate roles={["tenant_admin", "product_manager", "ml_operator", "device_operator", "auditor"]}>
                  <CabinetTypePage />
                </RoleGate>
              }
            />
            <Route
              path="cabinets/:deviceId/openings/:sessionId"
              element={
                <RoleGate roles={["tenant_admin", "device_operator", "auditor"]}>
                  <OpeningPage />
                </RoleGate>
              }
            />
            <Route
              path="users"
              element={
                <RoleGate roles={["tenant_admin"]}>
                  <UsersPage />
                </RoleGate>
              }
            />
            <Route
              path="integrations"
              element={
                <RoleGate roles={["tenant_admin", "auditor"]}>
                  <IntegrationsPage />
                </RoleGate>
              }
            />
            <Route
              path="cabinets/:deviceId"
              element={
                <RoleGate roles={["tenant_admin", "device_operator", "auditor"]}>
                  <CabinetPage />
                </RoleGate>
              }
            />
            <Route
              path="products"
              element={
                <RoleGate roles={["tenant_admin", "product_manager", "auditor"]}>
                  <ProductListPage />
                </RoleGate>
              }
            />
            {/* New products are created on the list (EAN, name) and get their photos on their page or in the app. */}
            <Route path="products/new" element={<Navigate to="/products" replace />} />
            <Route
              path="products/:id"
              element={
                <RoleGate roles={["tenant_admin", "product_manager", "auditor"]}>
                  <ProductDetailPage />
                </RoleGate>
              }
            />
            {/* The YOLO model builder is retired (docs/tasks/35): nothing on the current path uses its models. */}
            <Route path="models/builder" element={<Navigate to="/" replace />} />
            <Route path="models/gallery" element={<Navigate to="/" replace />} />
            <Route path="models/deployment" element={<Navigate to="/" replace />} />
            <Route path="devices" element={<Navigate to="/" replace />} />
            <Route
              path="devices/:deviceId/console"
              element={
                <RoleGate roles={["tenant_admin", "device_operator"]}>
                  <DeviceConsolePage />
                </RoleGate>
              }
            />
            <Route path="sessions" element={<Navigate to="/" replace />} />
            <Route
              path="purchases"
              element={
                <RoleGate roles={["tenant_admin", "auditor"]}>
                  <PurchasesPage />
                </RoleGate>
              }
            />
            <Route
              path="classifier"
              element={
                <RoleGate roles={["tenant_admin", "ml_operator", "device_operator", "auditor"]}>
                  <ClassifierPage />
                </RoleGate>
              }
            />
            <Route path="docs" element={<DocsPage />} />
            <Route path="docs/:docId" element={<DocsPage />} />
            <Route path="api-docs" element={<ApiDocsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route
              path="test"
              element={
                <RoleGate roles={["tenant_admin", "device_operator"]}>
                  <LiveTestPage />
                </RoleGate>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}
